import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Database, RotateCcw, Search } from 'lucide-react'
import { dataCubeApi } from '@/api/data-cube'
import { DateRangePicker } from '@/components/DateRangePicker'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { DataCubeListParams } from '@/types/data-cube'

export function DepartmentSummaryPage() {
	const [filters, setFilters] = useState<DataCubeListParams>({})
	const [searchFilters, setSearchFilters] = useState<DataCubeListParams>({})
	const { data: batches = [] } = useQuery({ queryKey: ['data-cube', 'batches'], queryFn: dataCubeApi.batches })
	const { data = [], isLoading, isFetching } = useQuery({
		queryKey: ['data-cube', 'department-summary', searchFilters],
		queryFn: () => dataCubeApi.departmentSummary(searchFilters),
	})

	const handleSearch = () => setSearchFilters(cleanParams(filters))
	const handleReset = () => {
		setFilters({})
		setSearchFilters({})
	}

	return (
		<div className="space-y-5">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">部门数据统计</h1>
				<p className="mt-1 text-sm text-muted-foreground">按部门汇总数据魔方运营指标</p>
			</div>

			<Card className="rounded-lg">
				<CardContent className="pt-6">
					<div className="flex flex-wrap items-center gap-3">
						<Select value={filters.batch_id ? String(filters.batch_id) : 'all'} onValueChange={(value) => setFilters({ ...filters, batch_id: value === 'all' ? undefined : Number(value) })}>
							<SelectTrigger className="w-[190px]">
								<SelectValue placeholder="全部批次" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">全部批次</SelectItem>
								{batches.map((batch) => (
									<SelectItem key={batch.id} value={String(batch.id)}>{batch.batch_name}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<DateRangePicker value={{ start_date: filters.start_date, end_date: filters.end_date }} onChange={(value) => setFilters({ ...filters, ...value })} />
						<Button type="button" onClick={handleSearch} disabled={isFetching}>
							<Search className="mr-1.5 h-4 w-4" />
							查询
						</Button>
						<Button type="button" variant="outline" onClick={handleReset}>
							<RotateCcw className="mr-1.5 h-4 w-4" />
							重置
						</Button>
					</div>
				</CardContent>
			</Card>

			<Card className="overflow-hidden rounded-lg">
				<CardContent className="p-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[1320px]">
							<TableHeader>
								<TableRow>
									<TableHead className="w-40">部门</TableHead>
									<TableHead>部门人数</TableHead>
									<TableHead>记录天数</TableHead>
									<TableHead>好友</TableHead>
									<TableHead>人均好友量</TableHead>
									<TableHead>加上的好友</TableHead>
									<TableHead>破冰</TableHead>
									<TableHead>破冰率</TableHead>
									<TableHead>礼物</TableHead>
									<TableHead>素颜照</TableHead>
									<TableHead>新开</TableHead>
									<TableHead>部门每天人均破冰</TableHead>
									<TableHead>部门每天人均好友量</TableHead>
									<TableHead>破冰/新开</TableHead>
									<TableHead>好友/新开</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{isLoading ? (
									<TableRow>
										<TableCell colSpan={15} className="h-28 text-center">加载中...</TableCell>
									</TableRow>
								) : data.length === 0 ? (
									<TableRow>
										<TableCell colSpan={15} className="h-32 text-center text-muted-foreground">
											<Database className="mx-auto mb-2 h-8 w-8" />
											暂无部门统计
										</TableCell>
									</TableRow>
								) : data.map((row) => (
									<TableRow key={row.department_id || 'summary'} className={row.department_id === 0 ? 'bg-muted/40 font-medium' : undefined}>
										<TableCell>{row.department_id === 0 ? '汇总' : row.department?.name || row.department_id}</TableCell>
										<TableCell>{row.department_user_count}</TableCell>
										<TableCell>{row.date_count}</TableCell>
										<TableCell>{row.friend_count}</TableCell>
										<TableCell>{row.friend_average}</TableCell>
										<TableCell>{row.crony_count}</TableCell>
										<TableCell>{row.breaking_count}</TableCell>
										<TableCell>{formatPercent(row.breaking_rate)}</TableCell>
										<TableCell>{row.gift_count}</TableCell>
										<TableCell>{row.photo_count}</TableCell>
										<TableCell>{row.newly_count}</TableCell>
										<TableCell>{formatNumber(row.breaking_daily_average)}</TableCell>
										<TableCell>{row.friend_daily_average}</TableCell>
										<TableCell>{formatNumber(row.breaking_newly_average)}</TableCell>
										<TableCell>{row.friend_newly_average}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

function cleanParams(params: DataCubeListParams): DataCubeListParams {
	return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '')) as DataCubeListParams
}

function formatNumber(value: number) {
	return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

function formatPercent(value: number) {
	return `${formatNumber(value)}%`
}
